import { h, clear, formatDuration, type Child } from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  textarea,
  table,
  emptyState,
  openModal,
  toast,
  badge,
  mediaChip,
  metric,
} from '../ui/components.js';
import { richTextEditor } from '../ui/rich-text.js';
import { openPrintWindow } from '../ui/print.js';
import { openMediaManager } from './media-manager.js';
import { api, ApiError } from '../api.js';
import { can } from '../store.js';
import { confirmTemplateRemoval } from './templates.js';
import type { Page, PageContext } from '../page.js';
import {
  ENCOUNTER_KIND_LABELS,
  MEDIA_KIND_LABELS,
  type MediaAsset,
  type MediaKind,
  type Template,
  type TemplateMinistration,
} from '../types.js';

export async function templateDetailPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Modelo', body: emptyState('Modelo não informado.') };

  const { template, ministrations } = await api.get<{
    template: Template;
    ministrations: TemplateMinistration[];
  }>(`/api/templates/${id}`);

  const writable = can.manageTemplates();
  const ordered = [...ministrations].sort(
    (a, b) => a.dayNumber - b.dayNumber || a.position - b.position,
  );
  const totalDays = ordered.reduce((max, item) => Math.max(max, item.dayNumber), 1);

  /**
   * Move uma ministração para cima/baixo **dentro do mesmo dia** e persiste
   * a nova ordem completa (a agenda de cada dia é independente).
   */
  const move = async (item: TemplateMinistration, direction: -1 | 1): Promise<void> => {
    const sameDay = ordered.filter((entry) => entry.dayNumber === item.dayNumber);
    const index = sameDay.findIndex((entry) => entry.id === item.id);
    const target = sameDay[index + direction];
    if (!target) return;

    const ids = ordered.map((entry) => entry.id);
    const from = ids.indexOf(item.id);
    const to = ids.indexOf(target.id);
    if (from < 0 || to < 0) return;

    const current = ids[from];
    const swap = ids[to];
    if (!current || !swap) return;
    ids[from] = swap;
    ids[to] = current;

    try {
      await api.post(`/api/templates/${id}/ministrations/reorder`, { order: ids });
      toast.success('Ordem atualizada', `Sequência do Dia ${item.dayNumber} ajustada.`);
      window.location.reload();
    } catch (error) {
      toast.error('Falha ao reordenar', error instanceof ApiError ? error.message : undefined);
    }
  };

  const rows = ordered.map((item) => {
    const sameDay = ordered.filter((entry) => entry.dayNumber === item.dayNumber);
    const indexInDay = sameDay.findIndex((entry) => entry.id === item.id);
    const isFirstInDay = indexInDay === 0;
    const isLastInDay = indexInDay === sameDay.length - 1;

    return [
      h(
        'div',
        { class: 'row', style: { gap: '8px' } },
        h('div', { class: 'list__index', text: String(indexInDay + 1) }),
        writable
          ? h(
              'div',
              { class: 'stack', style: { gap: '2px' } },
              h('button', {
                class: 'rte__btn',
                text: '▲',
                attrs: { type: 'button', title: 'Mover para cima (no mesmo dia)' },
                onclick: () => void move(item, -1),
              }),
              h('button', {
                class: 'rte__btn',
                text: '▼',
                attrs: { type: 'button', title: 'Mover para baixo (no mesmo dia)' },
                onclick: () => void move(item, 1),
              }),
            )
          : null,
      ),
      h(
        'div',
        { class: 'stack stack--sm' },
        h(
          'div',
          { class: 'row' },
          h('span', { style: { fontWeight: '600' }, text: item.title }),
          totalDays > 1 ? badge(`Dia ${item.dayNumber}`, 'accent') : null,
          isFirstInDay ? badge('Primeira do dia', 'neutral') : null,
          isLastInDay && sameDay.length > 1 ? badge('Última do dia', 'neutral') : null,
        ),
        h('div', { class: 'muted small', text: item.theme ?? 'Sem tema definido' }),
        h(
          'div',
          { class: 'row' },
          item.scriptHtml
            ? badge('Roteiro formatado', 'accent')
            : item.script
              ? badge('Roteiro simples', 'neutral')
              : badge('Sem roteiro', 'neutral'),
          item.script
            ? h('span', {
                class: 'faint small',
                text: `${item.script.slice(0, 80)}${item.script.length > 80 ? '…' : ''}`,
              })
            : null,
        ),
      ),
      h(
        'div',
        { class: 'stack stack--sm' },
        h('div', { text: formatDuration(item.suggestedDurationMin) }),
        item.intervalMin > 0
          ? h('div', {
              class: 'faint small',
              text: `+ ${formatDuration(item.intervalMin)} de intervalo`,
            })
          : h('div', { class: 'faint small', text: 'sem intervalo' }),
      ),
      h(
        'div',
        { class: 'table__actions' },
        button('Ver', {
          size: 'sm',
          variant: 'ghost',
          onClick: () =>
            openPrintWindow(
              item.title,
              item.scriptHtml ?? `<p>${escapeHtml(item.script || 'Roteiro não preenchido.')}</p>`,
              template.name,
            ),
        }),
        writable
          ? button('Editar', {
              size: 'sm',
              variant: 'ghost',
              onClick: () => openMinistrationModal(id, item, ordered.length),
            })
          : null,
        button('Materiais / mídias', {
          size: 'sm',
          variant: 'subtle',
          onClick: () => openMediaModal(item),
        }),
      ),
    ];
  });

  const totalSpeaking = ordered.reduce((sum, item) => sum + item.suggestedDurationMin, 0);
  const totalIntervals = ordered.reduce((sum, item) => sum + item.intervalMin, 0);

  const body: Child[] = [
    h(
      'div',
      { class: 'grid grid--4' },
      metric('Tipo de encontro', ENCOUNTER_KIND_LABELS[template.kind], 'accent'),
      metric('Ministrações', ordered.length, 'info'),
      metric('Tempo de fala', formatDuration(totalSpeaking)),
      metric('Intervalos', formatDuration(totalIntervals), 'warning'),
    ),
    card({
      title: 'Cronograma previsto pelo modelo',
      hint: 'Estes tempos e esta ordem geram a agenda de cada edição criada a partir do modelo.',
      body: h(
        'div',
        { class: 'stack' },
        h(
          'div',
          { class: 'row' },
          h('div', {
            class: 'muted small',
            text: `Duração total prevista: ${formatDuration(totalSpeaking + totalIntervals)}`,
          }),
          badge(`${ordered.length} etapa${ordered.length === 1 ? '' : 's'}`, 'neutral'),
          template.isOfficial ? badge('Oficial', 'success') : badge('Rascunho', 'neutral'),
        ),
        h(
          'div',
          { class: 'progress' },
          h('div', {
            class: 'progress__bar',
            style: {
              width: `${Math.min(
                100,
                Math.round((totalSpeaking / Math.max(1, totalSpeaking + totalIntervals)) * 100),
              )}%`,
            },
          }),
        ),
        h('div', {
          class: 'faint small',
          text: 'A barra representa o tempo de fala em relação ao tempo total (fala + intervalos).',
        }),
      ),
    }),
    card({
      title: 'Ministrações e ordem do cronograma',
      hint: writable
        ? 'Use ▲ ▼ para reordenar. A ordem é replicada para as edições e define a agenda. Cada ministração recebe seus materiais (slides, PDFs, imagens, YouTube e Spotify).'
        : 'Seu perfil tem acesso somente leitura ao modelo.',
      actions: writable
        ? [
            button('+ Ministração', {
              variant: 'ghost',
              size: 'sm',
              onClick: () => openMinistrationModal(id, null, ordered.length),
            }),
            button('Exportar roteiro (PDF)', {
              variant: 'subtle',
              size: 'sm',
              onClick: () => exportTemplateScript(template, ordered),
            }),
          ]
        : [
            button('Exportar roteiro (PDF)', {
              variant: 'ghost',
              size: 'sm',
              onClick: () => exportTemplateScript(template, ordered),
            }),
          ],
      body:
        rows.length > 0
          ? table(['Ordem', 'Ministração', 'Tempos', ''], rows)
          : emptyState('Este modelo ainda não possui ministrações.', '⧉'),
    }),
  ];

  const actions: ReturnType<typeof button>[] = [
    button('Voltar', { variant: 'ghost', size: 'sm', onClick: () => context.navigate('/modelos') }),
  ];

  if (writable) {
    actions.push(
      button('Excluir modelo', {
        variant: 'danger',
        size: 'sm',
        onClick: () =>
          confirmTemplateRemoval(template.name, async () => {
            await api.delete(`/api/templates/${id}`);
            toast.success('Modelo excluído');
            context.navigate('/modelos');
          }),
      }),
    );
  }

  return {
    title: template.name,
    subtitle: template.description ?? 'Modelo mestre de ministrações.',
    actions,
    body,
  };
}

/* ---------- Exportação do roteiro completo -------------------------------- */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Exporta todas as ministrações do modelo em um único PDF (padrão ABNT). */
function exportTemplateScript(template: Template, ordered: TemplateMinistration[]): void {
  if (ordered.length === 0) {
    toast.error('Modelo sem ministrações', 'Cadastre ao menos uma ministração.');
    return;
  }

  const body = ordered
    .map((item, index) => {
      const content =
        item.scriptHtml ??
        (item.script
          ? item.script
              .split(/\n{2,}/)
              .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
              .join('')
          : '<p><em>Roteiro não preenchido.</em></p>');

      const timing = `${formatDuration(item.suggestedDurationMin)}${
        item.intervalMin > 0 ? ` + ${formatDuration(item.intervalMin)} de intervalo` : ''
      }`;

      return `
        <h2>${index + 1}. ${escapeHtml(item.title)}</h2>
        <p><strong>Tema:</strong> ${escapeHtml(item.theme ?? 'não informado')} · <strong>Tempo:</strong> ${timing}</p>
        ${content}
      `;
    })
    .join('<hr />');

  openPrintWindow(
    `Roteiro — ${template.name}`,
    body,
    `${ordered.length} ministrações · ${ENCOUNTER_KIND_LABELS[template.kind]}`,
  );
}

/* ---------- Editar / criar ministração do modelo --------------------------- */

function openMinistrationModal(
  templateId: string,
  item: TemplateMinistration | null,
  currentCount: number,
): void {
  const titleInput = input({
    value: item?.title ?? '',
    placeholder: 'Ex.: Abertura, Palavra Central…',
  });
  const themeInput = input({ value: item?.theme ?? '', placeholder: 'Tema (opcional)' });
  const durationInput = input({
    type: 'number',
    value: String(item?.suggestedDurationMin ?? 45),
    attrs: { min: '5', max: '600' },
  });
  const intervalInput = input({
    type: 'number',
    value: String(item?.intervalMin ?? 0),
    attrs: { min: '0', max: '240' },
  });
  const positionInput = input({
    type: 'number',
    value: String(item?.position ?? currentCount + 1),
    attrs: { min: '1', max: '999' },
  });
  const dayInput = input({
    type: 'number',
    value: String(item?.dayNumber ?? 1),
    attrs: { min: '1', max: '60' },
  });

  // Editor de texto rico com formatação ABNT.
  const editor = richTextEditor({
    value: item?.scriptHtml ?? '',
    minHeight: 300,
    placeholder: 'Escreva o roteiro da ministração com a formatação desejada…',
    documentTitle: item?.title ?? 'Roteiro da ministração',
    documentSubtitle: 'Modelo mestre',
  });

  const errorSlot = h('div', { class: 'field__error' });

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    const payload = {
      title: titleInput.value.trim(),
      theme: themeInput.value.trim() || null,
      dayNumber: Number(dayInput.value) || 1,
      suggestedDurationMin: Number(durationInput.value) || 45,
      intervalMin: Number(intervalInput.value) || 0,
      position: Number(positionInput.value) || undefined,
      scriptHtml: editor.getHtml(),
      script: editor.getText(),
    };

    try {
      if (item) {
        await api.patch(`/api/template-ministrations/${item.id}`, payload);
        toast.success('Ministração atualizada', 'Roteiro e tempos salvos.');
      } else {
        await api.post(`/api/templates/${templateId}/ministrations`, payload);
        toast.success('Ministração adicionada', 'A ordem do cronograma foi atualizada.');
      }
      modal?.close();
      window.location.reload();
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao salvar.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: item ? `Editar ministração · ${item.title}` : 'Nova ministração do modelo',
    wide: true,
    body: [
      h(
        'div',
        { class: 'form-grid' },
        field({ label: 'Título', control: titleInput, required: true }),
        field({ label: 'Tema', control: themeInput }),
        field({
          label: 'Tempo de fala (min)',
          control: durationInput,
          required: true,
          hint: 'Duração estimada da ministração.',
        }),
        field({
          label: 'Intervalo após (min)',
          control: intervalInput,
          hint: 'Transição, coffee break ou momento de altar. Não conta como tempo de fala.',
        }),
        field({
          label: 'Dia do encontro',
          control: dayInput,
          required: true,
          hint: 'Encontros de vários dias: escolha em qual dia esta ministração acontece.',
        }),
        field({
          label: 'Posição no cronograma',
          control: positionInput,
          hint: 'Define a ordem na agenda do dia.',
        }),
      ),
      field({
        label: 'Roteiro formatado (ABNT)',
        control: editor.element,
        hint: 'Use "Corpo ABNT", "Título ABNT" e "Citação longa" para a formatação normativa. O botão "Exportar PDF" gera o documento em A4 com margens 3 cm × 2 cm.',
      }),
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button('Exportar PDF', {
        variant: 'ghost',
        onClick: () =>
          openPrintWindow(
            titleInput.value || 'Roteiro',
            editor.getHtml() || '<p>Roteiro não preenchido.</p>',
            'Modelo mestre',
          ),
      }),
      button(item ? 'Salvar' : 'Adicionar', { onClick: () => void submit() }),
    ],
  });
}

/* ---------- Materiais do modelo ------------------------------------------- */

function openMediaModal(ministration: TemplateMinistration): void {
  openMediaManager({
    title: `Materiais · ${ministration.title}`,
    subtitle:
      'Envie slides, PDFs, roteiros, imagens, áudio e vídeo — ou vincule links do YouTube e Spotify. Materiais oficiais são replicados para as novas edições.',
    scope: 'TEMPLATE_MINISTRATION',
    ownerId: ministration.id,
    basePath: `/api/template-ministrations/${ministration.id}/media`,
    canWrite: can.manageTemplates(),
    canIncorporate: false,
    readOnlyHint:
      'Somente Administrador Geral e Pastor podem alterar os materiais do modelo oficial.',
  });
}
